import React from "react";
import { Instagram, Youtube, Globe, Camera } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFacebook, faLinkedinIn, faPinterestP } from "@fortawesome/free-brands-svg-icons";
import { SiSubstack } from "react-icons/si";
import { cn } from "@/lib/utils";
import { PLATFORMS, type Platform } from "@/constants/platforms";

// Custom X (Twitter) logo component - exported for standalone use
export const XLogo = ({ size = 16, className }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// Custom Reddit logo component - exported for standalone use
export const RedditLogo = ({ size = 16, className }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 256 256" fill="currentColor" className={className}>
    <g
      transform="translate(1.4065934065934016 1.4065934065934016) scale(2.81 2.81)"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      paintOrder="stroke fill"
    >
      <path d="M 45.19 84.802 c -21.821 0 -39.58 -12.617 -39.63 -28.14 c -0.038 -0.584 -0.053 -1.17 -0.046 -1.757 c -0.654 -0.398 -1.28 -0.876 -1.87 -1.427 c -2.223 -2.082 -3.498 -4.902 -3.594 -7.943 c -0.096 -3.045 1 -5.944 3.086 -8.162 c 2.077 -2.217 4.897 -3.493 7.938 -3.589 c 2.517 -0.08 4.933 0.655 6.948 2.09 c 7.07 -4.481 15.131 -7.034 23.485 -7.437 l 4.429 -20.818 c 0.007 -0.054 0.016 -0.107 0.027 -0.156 c 0.228 -1.037 0.847 -1.923 1.742 -2.492 c 0.893 -0.569 1.957 -0.754 2.991 -0.523 L 66.49 7.604 c 1.059 0.212 1.747 1.242 1.535 2.302 c -0.211 1.059 -1.244 1.745 -2.301 1.535 L 49.799 8.259 c -0.004 0.021 -0.008 0.043 -0.012 0.063 l -4.292 20.175 c 8.266 0.582 19.14 3.267 26.48 7.764 c 1.829 -1.32 4.002 -2.075 6.258 -2.154 c 6.28 -0.203 11.576 4.711 11.808 10.976 c 0.07 3.952 -1.928 7.647 -5.176 9.771 c 0.009 0.604 -0.007 1.208 -0.046 1.808 C 84.767 72.184 67.01 84.802 45.19 84.802 z M 11.44 37.691 c -0.081 0 -0.162 0.001 -0.243 0.004 c -1.995 0.063 -3.845 0.9 -5.208 2.356 c -1.372 1.459 -2.091 3.362 -2.028 5.361 c 0.063 1.995 0.9 3.845 2.356 5.208 c 0.623 0.582 1.294 1.034 1.99 1.341 c 0.753 0.333 1.218 1.102 1.162 1.923 c -0.058 0.855 -0.058 1.722 0 2.578 c 0.003 0.044 0.004 0.089 0.004 0.133 c 0 13.396 16.022 24.294 35.717 24.294 c 19.694 0 35.717 -10.898 35.717 -24.294 c 0 -0.045 0.002 -0.089 0.005 -0.134 c 0.058 -0.855 0.058 -1.722 0 -2.577 c -0.054 -0.787 0.371 -1.53 1.077 -1.884 c 2.566 -1.283 4.192 -3.957 4.141 -6.812 c -0.151 -4.077 -3.647 -7.294 -7.762 -7.172 c -1.817 0.063 -3.559 0.802 -4.901 2.08 c -0.667 0.635 -1.688 0.72 -2.45 0.2 c -7.27 -4.952 -19.8 -7.814 -27.92 -7.998 c -8.622 0.146 -16.957 2.78 -24.104 7.616 c -0.759 0.512 -1.771 0.432 -2.437 -0.195 C 15.157 38.407 13.349 37.691 11.44 37.691 z M 44.004 75.379 c -5.779 0 -11.464 -1.896 -16.12 -5.396 c -0.864 -0.649 -1.037 -1.875 -0.388 -2.739 c 0.65 -0.865 1.875 -1.037 2.739 -0.389 c 4.232 3.181 9.455 4.818 14.712 4.59 c 0.054 -0.002 0.109 -0.002 0.164 0 c 5.261 0.205 10.484 -1.411 14.713 -4.59 c 0.593 -0.445 1.386 -0.519 2.05 -0.186 c 0.663 0.331 1.082 1.009 1.082 1.75 v 0.257 c 0 0.975 -0.715 1.785 -1.649 1.933 c -4.765 3.292 -10.51 4.961 -16.277 4.75 C 44.688 75.372 44.345 75.379 44.004 75.379 z" />
      <path d="M 23.453 51.63 c 0 -3.566 2.908 -6.475 6.475 -6.475 c 3.566 0 6.475 2.908 6.475 6.475 c 0 3.566 -2.908 6.475 -6.475 6.475 C 26.346 58.071 23.453 55.196 23.453 51.63 z" />
      <path d="M 60.115 58.57 c -0.112 0 -0.208 0 -0.322 0 l 0.049 -0.241 c -3.566 0 -6.475 -2.908 -6.475 -6.475 c 0 -3.566 2.908 -6.475 6.475 -6.475 s 6.475 2.908 6.475 6.475 C 66.461 55.421 63.681 58.425 60.115 58.57 z" />
      <path d="M 72.514 18.796 c -4.493 0 -8.148 -3.655 -8.148 -8.148 c 0 -4.493 3.655 -8.148 8.148 -8.148 s 8.148 3.655 8.148 8.148 C 80.662 15.141 77.006 18.796 72.514 18.796 z M 72.514 6.414 c -2.335 0 -4.235 1.9 -4.235 4.235 c 0 2.335 1.9 4.235 4.235 4.235 s 4.235 -1.9 4.235 -4.235 C 76.749 8.314 74.848 6.414 72.514 6.414 z" />
    </g>
  </svg>
);

// Custom TikTok logo component - exported for standalone use
export const TikTokLogo = ({ size = 16, className }: { size?: number; className?: string }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" />
  </svg>
);

/**
 * Get platform icon component based on platform type
 * Exported for standalone use outside of PlatformIcon component
 * @param platform - The platform type
 * @param size - Icon size in pixels
 * @param forceWhite - If true, icon is always white (for dark backgrounds). If false, adapts to light/dark mode.
 */
export const getPlatformIcon = (
  platform: Platform | null,
  size: number = 16,
  forceWhite: boolean = false,
): React.ReactNode => {
  const colorClass = forceWhite ? "text-white" : "text-foreground dark:text-white";

  if (!platform) return <Globe size={size} className={colorClass} />;
  switch (platform) {
    case PLATFORMS.INSTAGRAM:
      return <Instagram size={size} className={colorClass} />;
    case PLATFORMS.TIKTOK:
      return (
        <div className={colorClass}>
          <TikTokLogo size={size} />
        </div>
      );
    case PLATFORMS.TWITTER:
      return (
        <div className={colorClass}>
          <XLogo size={size} />
        </div>
      );
    case PLATFORMS.YOUTUBE:
      return <Youtube size={size} className={colorClass} />;
    case PLATFORMS.FACEBOOK:
      return (
        <FontAwesomeIcon
          icon={faFacebook}
          className={colorClass}
          style={{ width: Math.max(size, 16), height: Math.max(size, 16) }}
        />
      );
    case PLATFORMS.REDDIT:
      return (
        <div className={colorClass}>
          <RedditLogo size={size} />
        </div>
      );
    case PLATFORMS.LINKEDIN:
      return (
        <FontAwesomeIcon
          icon={faLinkedinIn}
          className={colorClass}
          style={{ width: size, height: size }}
        />
      );
    case PLATFORMS.PINTEREST:
      return (
        <FontAwesomeIcon
          icon={faPinterestP}
          className={colorClass}
          style={{ width: size, height: size }}
        />
      );
    case PLATFORMS.UPLOAD:
      return <Camera size={size} className={colorClass} />;
    case PLATFORMS.SUBSTACK:
      return <SiSubstack size={size} className={colorClass} />;
    case PLATFORMS.WEB:
      return <Globe size={size} className={colorClass} />;
    default:
      return <Globe size={size} className={colorClass} />;
  }
};

/**
 * Known social platform patterns - if URL/platform matches these, show their icon
 * Otherwise show web globe as fallback
 */
const SOCIAL_PLATFORMS = {
  instagram: {
    domains: ["instagram.com", "instagr.am", "cdninstagram.com", "fbsbx.com"],
    platform: PLATFORMS.INSTAGRAM,
  },
  tiktok: { domains: ["tiktok.com"], platform: PLATFORMS.TIKTOK },
  twitter: { domains: ["twitter.com", "x.com", "t.co"], platform: PLATFORMS.TWITTER },
  youtube: { domains: ["youtube.com", "youtu.be"], platform: PLATFORMS.YOUTUBE },
  facebook: { domains: ["facebook.com", "fb.com", "fb.watch", "fbcdn.net"], platform: PLATFORMS.FACEBOOK },
  reddit: { domains: ["reddit.com", "redd.it"], platform: PLATFORMS.REDDIT },
  linkedin: { domains: ["linkedin.com"], platform: PLATFORMS.LINKEDIN },
  substack: { domains: ["substack.com"], platform: PLATFORMS.SUBSTACK },
} as const;

/**
 * Try to detect a known social platform from URL
 */
function detectSocialPlatformFromUrl(url?: string | null): Platform | null {
  if (!url) return null;

  let hostname: string;
  try {
    hostname = new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }

  for (const social of Object.values(SOCIAL_PLATFORMS)) {
    const sourceHost = social.platform === PLATFORMS.SUBSTACK ? hostname.replace(/\.$/, "") : hostname;
    if (social.domains.some(d => sourceHost === d || sourceHost.endsWith(`.${d}`))) {
      return social.platform;
    }
  }

  return null;
}

/**
 * Try to detect a known social platform from platform/source string
 */
function detectSocialPlatformFromString(value?: string | null): Platform | null {
  if (!value || typeof value !== "string") return null;

  const normalized = value.toLowerCase();

  if (["substack", "substack.com"].includes(normalized.trim())) return PLATFORMS.SUBSTACK;
  if (normalized.includes("instagram")) return PLATFORMS.INSTAGRAM;
  if (normalized.includes("tiktok")) return PLATFORMS.TIKTOK;
  if (normalized.includes("twitter") || normalized === "x") return PLATFORMS.TWITTER;
  if (normalized.includes("youtube")) return PLATFORMS.YOUTUBE;
  if (normalized.includes("facebook")) return PLATFORMS.FACEBOOK;
  if (normalized.includes("reddit")) return PLATFORMS.REDDIT;
  if (normalized.includes("linkedin")) return PLATFORMS.LINKEDIN;
  if (normalized === "upload" || normalized === "personal_upload") return PLATFORMS.UPLOAD;

  return null;
}

export interface PlatformIconProps {
  canonicalUrl?: string | null;
  sourceUrl?: string | null;
  platform?: string | null;
  source?: string | { platform?: string } | null;
  className?: string;
  size?: number;
  position?: "top-left" | "top-right";
  /** Tight container with minimal padding for compact thumbnail overlays. */
  compact?: boolean;
}

/**
 * PlatformIcon component that displays the source platform icon
 * Shows known social platform icons, or web globe as fallback
 */
export const PlatformIcon = ({
  canonicalUrl,
  sourceUrl,
  platform,
  source,
  className,
  size = 14,
  position = "top-left",
  compact = false,
}: PlatformIconProps) => {
  // Extract source string from source object if needed
  const sourceString = typeof source === "string" ? source : source?.platform;

  // Try to detect a known social platform
  const detectedPlatform =
    detectSocialPlatformFromUrl(canonicalUrl) ||
    detectSocialPlatformFromUrl(sourceUrl) ||
    detectSocialPlatformFromString(platform) ||
    detectSocialPlatformFromString(sourceString);

  // If we detected a social platform, use it. Otherwise, if we have ANY indicator, show web globe
  const hasAnyIndicator = canonicalUrl || sourceUrl || platform || sourceString;

  if (!detectedPlatform && !hasAnyIndicator) {
    return null;
  }

  const finalPlatform = detectedPlatform || PLATFORMS.WEB;

  const positionClasses = {
    "top-left": "left-3 top-3",
    "top-right": "right-3 top-3",
  };
  const containerPadding = compact ? 6 : 16;

  return (
    <div
      className={cn(
        "absolute z-10 flex items-center justify-center rounded-xl ",
        "bg-black/50 dark:bg-black/60",
        "border border-white/30 dark:border-white/25",
        "",
        positionClasses[position],
        className,
      )}
      style={{ width: size + containerPadding, height: size + containerPadding }}
    >
      {getPlatformIcon(finalPlatform, size, true)}
    </div>
  );
};
